import { Box, Stack, styled } from '@mui/material'
import { FileDropArea, FormTextField, Modal, tokens, UploadErrorAlert, WHITE_LISTED_FILES } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza/text/Typography/Typography'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { Paperclip, UploadSimple, X } from '@phosphor-icons/react'
import { useFieldArray, useForm } from 'react-hook-form'
import { SelectTags } from 'ComponentLibrary/src/molecules/SelectTags'
import { DocumentTagOption, tagOptions as defaultTagOptions } from 'ComponentLibrary/src/molecules/SelectTags/constants'
import { IconButton } from 'ComponentLibrary/src/piazza/buttons/IconButton/IconButton'
import { Document, DocumentWithIndex, UploadFileFormType } from 'ComponentLibrary/src/molecules/FileUploadModal/types'
import { isEmpty } from 'lodash'
import { StyledLoadingDotsWrapper } from 'ComponentLibrary/src/atoms/LoadingIndicator/StyledLoadingDotsWrapper'
import { translateMimeType } from 'ComponentLibrary/src/common/files'
import { Link } from 'ComponentLibrary/src/piazza/text/Link/Link'
import { CounterDot } from 'ComponentLibrary/src/piazza/content/CounterDot/CounterDot'
import { type UploadErrorAlertCopy } from 'ComponentLibrary/src/molecules/UploadErrorAlert/UploadErrorAlert'

export type FileUploadModalCopy = {
    uploadTitle: string
    editTitle: string
    createButton: string
    confirmButton: string
    cancelButton: string
    dropzonePrompt: string
    nameLabel: string
    tagsLabel: string
    unknownMimeTypeLabel: string
    uploadErrorAlert: UploadErrorAlertCopy
}

const DEFAULT_FILE_UPLOAD_MODAL_COPY: FileUploadModalCopy = {
    uploadTitle: 'Dokumente hochladen',
    editTitle: 'Dokumente bearbeiten',
    createButton: 'Erstellen',
    confirmButton: 'Bestätigen',
    cancelButton: 'Abbrechen',
    dropzonePrompt: 'Datei auswählen oder per Drag & Drop hochladen',
    nameLabel: 'Name',
    tagsLabel: 'Tags',
    unknownMimeTypeLabel: 'Unbekannt',
    uploadErrorAlert: {
        title: 'Fehler beim Hochladen der Dateien',
        description: 'Folgende Dateien konnten nicht erfolgreich hochgeladen werden:',
        supportText: 'Bitte versuchen Sie es erneut oder schreiben Sie eine Nachricht an support@einwert.com',
    },
}

type FileUploadModalProps = {
    isOpen: boolean
    isEditing: boolean
    close: () => void
    updateDocuments: (documents: Document[], onSuccess: () => void) => void
    headerAdornment?: ReactNode
    isPersisting?: boolean
    isUploading?: boolean
    numDocumentsToUpload?: number
    uploadDocuments?: (documents: File[]) => void
    failedDocumentUploads?: string[]
    isDownloadingDocument?: boolean
    downloadDocument: (documentId: string) => void
    tagOptions?: DocumentTagOption[]
    copy?: Partial<Omit<FileUploadModalCopy, 'uploadErrorAlert'>> & {
        uploadErrorAlert?: Partial<UploadErrorAlertCopy>
    }
} & (
    | {
          deleteDocument: (documentIndex: number) => void
          documents: DocumentWithIndex[]
      }
    | {
          deleteDocument?: never
          documents: Document[]
      }
)

export const FileUploadModal = ({
    isOpen,
    close,
    isEditing,
    isPersisting,
    isUploading,
    documents,
    updateDocuments,
    uploadDocuments,
    failedDocumentUploads,
    deleteDocument,
    downloadDocument,
    isDownloadingDocument,
    headerAdornment,
    tagOptions = defaultTagOptions,
    copy,
}: FileUploadModalProps) => {
    const [isOver, setIsOver] = useState(false)
    const modalRef = useRef<HTMLDivElement>(null)
    const uploadDocumentRef = useRef<HTMLInputElement>(null)
    const resolvedCopy = {
        ...DEFAULT_FILE_UPLOAD_MODAL_COPY,
        ...copy,
        uploadErrorAlert: {
            ...DEFAULT_FILE_UPLOAD_MODAL_COPY.uploadErrorAlert,
            ...copy?.uploadErrorAlert,
        },
    }
    const {
        control,
        handleSubmit,
        formState: { errors, isDirty },
        reset,
    } = useForm<UploadFileFormType>({
        defaultValues: {
            documents,
        },
    })

    const [initialDocuments, setInitialDocuments] = useState<Document[]>([])
    useEffect(() => {
        setInitialDocuments(documents)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'documents',
    })

    // Synchronize the fields array with the documents array whenever documents change.
    // This ensures the fields array stays in sync with the documents array, while maintaining the form's state consistency.
    useEffect(() => {
        documents.forEach((document) => {
            if (!fields.some((field) => field.uuid === document.uuid)) {
                append(document)
            }
        })
        fields.forEach((field, index) => {
            if (!documents.some((document) => document.uuid === field.uuid)) {
                remove(index)
            }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documents, isEditing])

    const onFormSubmit = (data: UploadFileFormType) => {
        updateDocuments(data.documents, () => {
            setInitialDocuments(documents)
            close()
        })
    }

    const closeAndReset = () => {
        setInitialDocuments(documents)
        close()
        reset()
    }

    return (
        <Modal
            ref={modalRef}
            onClose={closeAndReset}
            showLoading={isPersisting || isDownloadingDocument}
            isOpen={isOpen}
            title={
                <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                    <Typography variant="headline1" color={tokens.ColorTextBoldest}>
                        {isEditing ? resolvedCopy.editTitle : resolvedCopy.uploadTitle}
                    </Typography>
                    {headerAdornment}
                </Stack>
            }
            footerProps={{
                primaryButton: {
                    text: isEditing ? resolvedCopy.confirmButton : resolvedCopy.createButton,
                    onClick: handleSubmit(onFormSubmit),
                    disabled: isUploading || isPersisting || !isDirty,
                },
                secondaryButton: {
                    text: resolvedCopy.cancelButton,
                    onClick: closeAndReset,
                    disabled: isPersisting,
                },
            }}
            showCloseIcon
            disableBackdropClose
        >
            <Stack direction="column" width="100%" justifyContent="space-evenly">
                {!isEditing && (
                    <>
                        {uploadDocuments && (
                            <StyledBox
                                bgcolor={isUploading ? tokens.ColorBackgroundDark : tokens.ColorBackgroundLight}
                                onClick={() => uploadDocumentRef?.current?.click()}
                            >
                                <FileDropArea
                                    dragAreaRef={modalRef}
                                    isOver={isOver}
                                    setIsOver={setIsOver}
                                    onDataDrop={(_areaId?: string, files?: File[]) => {
                                        !isUploading && isOver && files && uploadDocuments?.(files)
                                    }}
                                />
                                {isUploading && <StyledLoadingDotsWrapper />}
                                <input
                                    hidden
                                    ref={uploadDocumentRef}
                                    multiple
                                    onChange={(e) => {
                                        e.target.files && uploadDocuments(Array.from(e.target.files))
                                        e.target.value = ''
                                    }}
                                    id="contained-button-file"
                                    type="file"
                                    accept={WHITE_LISTED_FILES}
                                />
                                <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" height="100%">
                                    {!isUploading && (
                                        <>
                                            <Typography variant="textM" color="textSecondary">
                                                {resolvedCopy.dropzonePrompt}
                                            </Typography>
                                            <IconButton
                                                variant="secondary"
                                                onClick={() => {}}
                                                icon={<UploadSimple size={tokens.SizingIconS} weight="bold" />}
                                                size="small"
                                            />
                                        </>
                                    )}
                                </Stack>
                            </StyledBox>
                        )}
                        {failedDocumentUploads && !isEmpty(failedDocumentUploads) && (
                            <UploadErrorAlert failedUploads={failedDocumentUploads} copy={resolvedCopy.uploadErrorAlert} />
                        )}
                    </>
                )}

                <Stack direction="column" gap={tokens.SizingSpacing200} width="100%" height="fit-content">
                    {fields?.length > 0 &&
                        fields.map(({ name, uuid, content_type }, index) => {
                            // Only render new documents in the form for uploading
                            if (initialDocuments.some((initialDocument) => initialDocument.uuid === uuid) && !isEditing) {
                                return null
                            }
                            return (
                                <>
                                    <DocumentStack direction="row" marginTop={!isEditing ? tokens.SizingSpacing400 : undefined}>
                                        <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                                            {fields?.length > 1 && <CounterDot number={index + 1} />}
                                            <Stack direction="column" gap={tokens.SizingSpacing050}>
                                                <Box onClick={() => downloadDocument(uuid)} sx={{ maxWidth: '432px' }}>
                                                    <Link href="#" ellipsis label={name} />
                                                </Box>
                                                <Stack direction="row" gap={tokens.SizingSpacing050}>
                                                    <Paperclip size={tokens.SizingIconS} color={tokens.ColorIconDefault} weight="bold" />
                                                    <Typography variant="textS" color={tokens.ColorTextDefault}>
                                                        {translateMimeType(content_type, resolvedCopy.unknownMimeTypeLabel)}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                        </Stack>
                                        <IconButton
                                            icon={<X size={tokens.SizingIconM} />}
                                            variant="tertiary"
                                            size="medium"
                                            onClick={() => deleteDocument?.(index)}
                                        />
                                    </DocumentStack>
                                    <FormTextField
                                        control={control}
                                        fieldName={`documents.${index}.name`}
                                        errors={errors}
                                        label={resolvedCopy.nameLabel}
                                        xs={12}
                                        textAlign="left"
                                    />
                                    <SelectTags control={control} index={index} label={resolvedCopy.tagsLabel} options={tagOptions} />
                                </>
                            )
                        })}
                </Stack>
            </Stack>
        </Modal>
    )
}

const StyledBox = styled(Box)(() => ({
    height: '100px',
    width: '100%',
    border: '1px dashed',
    borderColor: tokens.ColorBorderDefault,
    borderRadius: tokens.SizingCornerM,
    padding: tokens.SizingSpacing200,
    textAlign: 'justify',
    position: 'relative',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
        backgroundColor: tokens.ColorBackgroundDark,
    },
}))

const DocumentStack = styled(Stack)(() => ({
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.SizingSpacing150,
    width: '100%',
    backgroundColor: tokens.ColorBackgroundDark,
    borderRadius: tokens.SizingCornerM,
    '&:not(:first-of-type)': {
        marginTop: tokens.SizingSpacing400,
    },
}))
